/* globals google */

const { geocodeAddress } = require('../common/utils/mapsApi');
const {
  getSiteCountryDefaultPosition,
} = require('../common/utils/geolocation');

const mapDefaultCenter = { lat: 37.0902, lng: -95.712891 };

// @type {Object<storeId, marker>}
let markersMap = {};
let map;
let mapLocationsBoundsFirstRun = true;
let mapLocationsBounds;

const mqlTwoColumnsLayout = window.matchMedia(
  `(min-width: ${window.FRONT_CONFIG.responsive.bp_900}px)`
);

let $container;
const storeLocatorContainerSelector = '.store-locator-grid';
const elementSelectors = {
  $mapCanvas: '.map-canvas',
  $tabMap: '[href="#storelocator-map"]',
};
const elements = {
  $mapCanvas: '.map-canvas',
  $tabMap: '[href="#storelocator-map"]',
};

function queryStoreLocatorElements() {
  $container = $(storeLocatorContainerSelector);
  Object.keys(elementSelectors).forEach((key) => {
    elements[key] = $container.find(elementSelectors[key]);
  });
}

function centerMapInStore(storeId) {
  const marker = markersMap[storeId];
  if (map && marker) {
    map.setCenter(marker.getPosition());
  }
}

function createGoogleMap(center) {
  if (!isGoogleMapsApiLoaded()) return null;

  const mapOptions = {
    zoom: 5,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false,
    center: mapDefaultCenter,
    gestureHandling: 'cooperative',
    maxZoom: 17,
    zoomControlOptions: {
      position: google.maps.ControlPosition.TOP_LEFT,
    },
    styles: [
      {
        featureType: 'landscape',
        elementType: 'labels',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'transit',
        elementType: 'labels',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'water',
        elementType: 'labels',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'road',
        elementType: 'labels.icon',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        stylers: [
          {
            hue: '#00aaff',
          },
          {
            saturation: -100,
          },
          {
            gamma: 2.15,
          },
          {
            lightness: 12,
          },
        ],
      },
      {
        featureType: 'road',
        elementType: 'labels.text.fill',
        stylers: [
          {
            visibility: 'on',
          },
          {
            lightness: 24,
          },
        ],
      },
      {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [
          {
            lightness: 57,
          },
        ],
      },
    ],
  };

  return new google.maps.Map(elements.$mapCanvas[0], {
    ...mapOptions,
    center,
  });
}

/**
 * Creates a Google Maps marker for a store
 *
 * @param {google.maps.LatLng|Object} latLng - Position of the marker (LatLng instance or literal)
 * @param {string} storeId - Unique identifier of the store
 * @param {string} storeName - Name of the store (used as marker title)
 * @param {boolean} isUnavailable - Indicates if the store is unavailable (capacity full or express out of stock)
 * @returns {google.maps.Marker|null} The created marker instance or null if Google Maps API is not loaded
 */
function createGoogleMarker(latLng, storeId, storeName, isUnavailable) {
  if (!isGoogleMapsApiLoaded()) return null;
  return new google.maps.Marker({
    position: latLng,
    map,
    title: storeName,
    icon: createGoogleMarkerIcon(false, isUnavailable),
    storeId,
  });
}

/**
 * Creates the icon configuration for a Google Maps marker
 *
 * @param {boolean} [selected=false] - Whether the marker is in selected state (affects scale)
 * @param {boolean} [isUnavailable=false] - Indicates if the store is unavailable (capacity full or express out of stock)
 * @returns {google.maps.Symbol|null} Marker icon configuration object or null if Google Maps API is not loaded
 */
function createGoogleMarkerIcon(selected = false, isUnavailable = false) {
  if (!isGoogleMapsApiLoaded()) return null;
  return {
    path: 'M23.317 35.43c-6.69 0-12.112-5.422-12.112-12.112 0-6.689 5.422-12.111 12.112-12.111 6.689 0 12.111 5.422 12.111 12.111-.008 6.686-5.426 12.104-12.111 12.112m0-35.43C10.445.015.014 10.447 0 23.318c-.013 3.282.682 6.526 2.04 9.514 5.828 12.756 17.007 26.225 20.294 30.05.468.543 1.288.603 1.83.136.05-.042.093-.088.136-.136 3.287-3.823 14.463-17.292 20.294-30.05 1.36-2.988 2.055-6.236 2.04-9.517C46.615 10.445 36.185.015 23.316 0',
    fillColor: isUnavailable ? ' #a8a8a8' : '#0027FF',
    fillOpacity: 1,
    scale: selected === true ? 0.65 : 0.35,
    anchor: new google.maps.Point(9, 24),
  };
}

function fitBoundsOnLocations() {
  if (mapLocationsBoundsFirstRun && mapLocationsBounds) {
    map.fitBounds(mapLocationsBounds);
  }
  mapLocationsBoundsFirstRun = false;
}

function getLocationsData() {
  const locationsData = elements.$mapCanvas.attr('data-locations');
  if (locationsData !== 'null' && typeof locationsData !== 'undefined') {
    return JSON.parse(locationsData);
  }
  return [];
}

function isGoogleMapsApiLoaded() {
  return typeof google !== 'undefined';
}

function isMapVisible() {
  return (
    mqlTwoColumnsLayout.matches ||
    elements.$tabMap.hasClass('active') ||
    elements.$tabMap[0]?.getAttribute('aria-selected') === 'true'
  );
}

function isMarkerVisible(storeId) {
  const marker = markersMap[storeId];
  return map?.getBounds()?.contains(marker.getPosition()) ?? false;
}

/**
 * Uses google maps api to render a map
 * @param {Object} centerLatLng - location object ({ lat, lng }) to center the map.
 */
async function renderMap(selectLocationFn, centerLatLng) {
  if (!isGoogleMapsApiLoaded()) return;
  // @type {Object<storeId, marker>}
  markersMap = {};
  mapLocationsBoundsFirstRun = true;

  // Center
  let center;
  if (centerLatLng) {
    center = new google.maps.LatLng(centerLatLng.lat, centerLatLng.lng);
  } else if (map) {
    center = map.getCenter(); // Previous map center if it exists
  } else {
    const coord = await getSiteCountryDefaultPosition();
    if (coord) {
      center = new google.maps.LatLng(coord.lat, coord.lng);
    }
  }

  map = createGoogleMap(center);

  const mapLocations = getLocationsData();
  mapLocationsBounds = new google.maps.LatLngBounds();

  mapLocations.forEach((item) => {
    const storeLocation = new google.maps.LatLng(item.latitude, item.longitude);
    const marker = createGoogleMarker(
      storeLocation,
      item.id,
      item.name,
      item.isCcCapacityFull || item.isCcExpressOutOfStock
    );

    marker.addListener('click', function handleMarkerClick() {
      selectLocationFn(this.storeId);
    });

    markersMap[item.id] = marker;

    // Create a minimum bound based on a set of storeLocations
    mapLocationsBounds.extend(marker.position);
  });
  // Fit the all the store marks in the center of a minimum bounds when any store has been found.
  if (mapLocations.length > 0 && isMapVisible()) {
    fitBoundsOnLocations();
  }
}

function setLocationsData(locations) {
  elements.$mapCanvas.attr('data-locations', locations);
}

/**
 * Updates the icon of an existing marker based on its state
 *
 * @param {string} storeId - Unique identifier of the store
 * @param {boolean} [selected=false] - Whether the marker is selected
 * @param {boolean} [isUnavailable=false] - Indicates if the store is unavailable (capacity full or express out of stock)
 * @returns {void}
 */
function updateMarkerIcon(storeId, selected = false, isUnavailable = false) {
  const marker = markersMap[storeId];
  if (marker) {
    marker.setIcon(createGoogleMarkerIcon(selected, isUnavailable));
  }
}

module.exports = {
  init() {
    queryStoreLocatorElements();
  },
  centerMapInStore,
  fitBoundsOnLocations,
  getLocationsData,
  isMarkerVisible,
  geocodeAddress,
  renderMap,
  setLocationsData,
  updateMarkerIcon,
};
